import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import "./SpendingPrediction.css";
const REACT_APP_EXPRESS_API = process.env.REACT_APP_EXPRESS_API;

const API_URL = `${REACT_APP_EXPRESS_API}/api/transactions/predictive`;

/* ===== util bulan untuk dropdown ===== */
const monthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
};
const compareMonthKey = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/* ===== Tooltip sesuai mockup: kartu putih, label kiri, nilai kanan tebal ===== */
function PredTooltip({ active, payload, label }) {
  if (!(active && payload && payload.length)) return null;
  const val = payload[0].value || 0;
  const tgl = new Date(label).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const currency = (n) =>
    `Rp${Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;

  return (
    <div className="ptt">
      <div className="ptt-row">
        <span className="ptt-label">Tanggal</span>
        <span className="ptt-value">{tgl}</span>
      </div>
      <div className="ptt-row">
        <span className="ptt-label">Nominal</span>
        <span className="ptt-value">{currency(val)}</span>
      </div>
    </div>
  );
}

export default function PredictionTrend() {
  const [rows, setRows] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");

  /* fetch data (fungsi ambil data kamu sudah benar; ini hanya mapping & UI) */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(API_URL, {
          method: "GET",
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status} - ${res.statusText}`);
        }

        const raw = await res.json();
        const list = Array.isArray(raw?.predictions) ? raw.predictions : [];

        const mapped = list
          .map((d) => ({
            date: d.date,
            dateObj: d?.date ? new Date(d.date) : null,
            predNominal: Number(d.predNominal || 0),
          }))
          .filter((r) => r.dateObj instanceof Date && !isNaN(r.dateObj));

        setRows(mapped);

        const months = new Set();
        mapped.forEach((r) => months.add(monthKey(r.dateObj)));
        const arr = Array.from(months).sort(compareMonthKey);
        setMonthOptions(arr);
        if (arr.length) {
          setStartMonth(arr[0]);
          setEndMonth(arr[arr.length - 1]);
        }
      } catch (e) {
        console.error("Gagal memuat tren prediksi:", e);
      }
    })();
  }, []);

  /* filter berdasar rentang bulan */
  const filtered = useMemo(() => {
    if (!startMonth || !endMonth) return rows;
    const [sY, sM] = startMonth.split("-").map(Number);
    const [eY, eM] = endMonth.split("-").map(Number);
    const start = new Date(sY, sM - 1, 1, 0, 0, 0, 0);
    const end = new Date(eY, eM, 0, 23, 59, 59, 999);
    return rows.filter((r) => r.dateObj >= start && r.dateObj <= end);
  }, [rows, startMonth, endMonth]);

  const formatXAxis = (iso) =>
    new Date(iso).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  return (
    <div className="prediction-section">
      <div className="prediction-panel">
        <h3 className="prediction-title">Analisis Tren Prediktif</h3>
        {/* Dropdown rentang bulan */}
        <div className="pred-range">
          <div className="pred-range-field">
            <label className="pred-range-label">Start Date</label>
            <select
              className="pred-range-select"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </div>

          <div className="pred-range-field">
            <label className="pred-range-label">End Date</label>
            <select
              className="pred-range-select"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Chart ala “Detail Transaksi” tapi warna oranye */}
        <div className="prediction-chart-card">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={filtered}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={formatXAxis}
                tickMargin={8}
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis
                tickFormatter={(n) => `Rp${(n / 1000).toFixed(0)}K`}
                width={72}
                stroke="#6b7280"
                fontSize={12}
              />
              <Tooltip content={<PredTooltip />} />
              <Line
                type="monotone"
                dataKey="predNominal"
                stroke="#F59E0B" /* oranye */
                strokeWidth={4}
                dot={{ r: 4, strokeWidth: 2, stroke: "#F59E0B", fill: "#fff" }}
                activeDot={{
                  r: 6,
                  fill: "#F59E0B",
                  stroke: "#FBBF24",
                  strokeWidth: 2,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
